import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AuthenticationGuardFn } from "./global-service/authentication.guard";
import { TenantHostnameGuardFn } from "./global-service/tenant-hostname.guard";
import { TenantSwitchGuardFn } from "./global-service/tenant-switch.guard";
import { LayoutComponent } from "./shared/layout/layout.component";
import { AreaSelectionGuardFn, AreaSelectionPageGuardFn } from "./global-service/area-selection.guard";
import { PermissionGuardFn } from "./global-service/permission.guard";
import { InternalMaintenanceGuardFn } from "./global-service/internal-maintenance.guard";
import { AppActions } from "tht-impianti-cl";

export namespace UrlCollection {
  export const localAuthentication = "login";
  export const restorePassword = "restore-password";
  export const changePassword = "change-password";
  export const homepage = "homepage";
  export const areaSelection = "area-selection";
  export const notAuthorized = "notAuthorized";
  export const notFound = "notFound";
  export const redirectTo = "";
  export const redirectRequest = "redirectRequest";
  export const forbidden = "403";
  export const organizations = "organizations";
  export const facilities = "facilities";
  export const types = "types";
  export const tickets = "tickets";
  export const users = "users";
  export const userApprovals = "user-approvals";
  export const components = "components";
  export const areas = "areas";
  export const account = "account";
  export const feedDemo = "feed-demo";
  export const ticketTransferDemo = "ticket-transfer-demo";
  export const genericErrorPage = "page-error";
  export const maintenanceContracts = "maintenance-contracts";
  export const internalMaintenances = "internal-maintenances";
  export const sharedChecklists = "shared-checklists";
}

const routes: Routes = [
  { path: "", redirectTo: UrlCollection.homepage, pathMatch: "full" },
  {
    path: UrlCollection.localAuthentication,
    loadChildren: async () => {
      const m = await import("./pages/local-authentication/local-authentication.module");
      return m.LocalAuthenticationModule;
    }
  },
  {
    path: UrlCollection.restorePassword,
    loadComponent: async () => {
      const m = await import("./pages/restore-password-form/restore-password-form.component");
      return m.RestorePasswordFormComponent;
    }
  },
  {
    path: `${UrlCollection.changePassword}/:token`,
    loadComponent: async () => {
      const m = await import("./pages/change-password/change-password.component");
      return m.ChangePasswordComponent;
    }
  },
  {
    path: UrlCollection.forbidden,
    loadComponent: async () => {
      const m = await import("./pages/forbidden/forbidden.component");
      return m.ForbiddenComponent;
    }
  },
  {
    path: UrlCollection.genericErrorPage,
    loadComponent: async () => {
      const m = await import("./pages/hostname-mismatch/hostname-mismatch.component");
      return m.HostnameMismatchComponent;
    }
  },
  {
    path: UrlCollection.areaSelection,
    loadComponent: async () => {
      const m = await import("./pages/area-selection/area-selection.component");
      return m.AreaSelectionComponent;
    },
    canActivate: [AuthenticationGuardFn, TenantHostnameGuardFn, AreaSelectionPageGuardFn]
  },
  {
    path: "",
    component: LayoutComponent,
    canActivate: [TenantSwitchGuardFn, AuthenticationGuardFn, TenantHostnameGuardFn, AreaSelectionGuardFn],
    children: [
      {
        path: UrlCollection.homepage,
        loadComponent: async () => {
          const m = await import("./pages/homepage/homepage.component");
          return m.HomepageComponent;
        }
      },
      {
        path: UrlCollection.organizations,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_ORGANIZATION_LIST },
        loadComponent: async () => {
          const m = await import("./pages/organizations-page/organizations-page.component");
          return m.OrganizationsPageComponent;
        }
      },
      {
        path: `${UrlCollection.organizations}/:id`,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_ORGANIZATION },
        loadComponent: async () => {
          const m = await import("./pages/organizations-details/organizations-details.component");
          return m.OrganizationsDetailsComponent;
        }
      },
      {
        path: UrlCollection.facilities,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_FACILITY_LIST },
        loadComponent: async () => {
          const m = await import("./pages/facilities-page/facilities-page.component");
          return m.FacilitiesPageComponent;
        }
      },
      {
        path: `${UrlCollection.facilities}/:id`,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_FACILITY },
        loadComponent: async () => {
          const m = await import("./pages/facilities-details/facilities-details.component");
          return m.FacilitiesDetailsComponent;
        }
      },
      {
        // Tipi (impianto e componente): si creano e si modificano da una sidenav della lista,
        // quindi non esiste una pagina di dettaglio e nemmeno un path `/:id`.
        path: UrlCollection.types,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_FACILITY },
        loadComponent: async () => {
          const m = await import("./pages/types-page/types-page.component");
          return m.TypesPageComponent;
        }
      },
      {
        path: UrlCollection.tickets,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_TICKET_LIST },
        loadComponent: async () => {
          const m = await import("./pages/tickets-page/tickets-page.component");
          return m.TicketsPageComponent;
        }
      },
      {
        path: `${UrlCollection.tickets}/:id`,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_TICKET },
        loadComponent: async () => {
          const m = await import("./pages/tickets-details/tickets-details.component");
          return m.TicketsDetailsComponent;
        }
      },
      {
        path: UrlCollection.users,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_USER_LIST },
        loadComponent: async () => {
          const m = await import("./pages/users-page/users-page.component");
          return m.UsersPageComponent;
        }
      },
      {
        path: `${UrlCollection.users}/:id`,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_USER },
        loadComponent: async () => {
          const m = await import("./pages/users-details/users-details.component");
          return m.UsersDetailsComponent;
        }
      },
      {
        path: UrlCollection.components,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_FACILITY_COMPONENT_LIST },
        loadComponent: async () => {
          const m = await import("./pages/components-page/components-page.component");
          return m.ComponentsPageComponent;
        }
      },
      {
        path: `${UrlCollection.components}/:id`,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_FACILITY_COMPONENT },
        loadComponent: async () => {
          const m = await import("./pages/components-details/components-details.component");
          return m.ComponentsDetailsComponent;
        }
      },
      {
        path: UrlCollection.areas,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_AREA_LIST },
        loadComponent: async () => {
          const m = await import("./pages/areas-page/areas-page.component");
          return m.AreasPageComponent;
        }
      },
      {
        path: `${UrlCollection.areas}/:id`,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_AREA },
        loadComponent: async () => {
          const m = await import("./pages/areas-details/areas-details.component");
          return m.AreasDetailsComponent;
        }
      },
      {
        path: UrlCollection.account,
        loadComponent: async () => {
          const m = await import("./pages/account-page/account-page.component");
          return m.AccountPageComponent;
        }
      },
      {
        path: UrlCollection.maintenanceContracts,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_MAINTENANCE_CONTRACT_LIST },
        loadComponent: async () => {
          const m = await import("./pages/maintenance-contracts-page/maintenance-contracts-page.component");
          return m.MaintenanceContractsPageComponent;
        }
      },
      {
        path: `${UrlCollection.maintenanceContracts}/:id`,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.READ_MAINTENANCE_CONTRACT },
        loadComponent: async () => {
          const m = await import("./pages/maintenance-contracts-details/maintenance-contracts-details.component");
          return m.MaintenanceContractsDetailsComponent;
        }
      },
      /*
       * MANUTENZIONI INTERNE — le stesse due pagine dei contratti, in modalità interna.
       *
       * `internal: true` non è decorazione: le due pagine lo leggono da
       * `ActivatedRoute.snapshot.data` e ne ricavano etichette, colonne, endpoint e rotte. Duplicare
       * i componenti avrebbe voluto dire mantenere in parallelo un dettaglio da quasi 4000 righe.
       *
       * `InternalMaintenanceGuardFn` accanto a `PermissionGuardFn`: la seconda verifica il
       * permesso, la prima il CONTESTO (mai Fornitore, nemmeno per il SuperAdmin). Vedi il JSDoc
       * della guardia per il perché non basti l'azione.
       */
      {
        path: UrlCollection.internalMaintenances,
        canActivate: [PermissionGuardFn, InternalMaintenanceGuardFn],
        data: { action: AppActions.READ_INTERNAL_MAINTENANCE_LIST, internal: true },
        loadComponent: async () => {
          const m = await import("./pages/maintenance-contracts-page/maintenance-contracts-page.component");
          return m.MaintenanceContractsPageComponent;
        }
      },
      {
        path: `${UrlCollection.internalMaintenances}/:id`,
        canActivate: [PermissionGuardFn, InternalMaintenanceGuardFn],
        data: { action: AppActions.READ_INTERNAL_MAINTENANCE, internal: true },
        loadComponent: async () => {
          const m = await import("./pages/maintenance-contracts-details/maintenance-contracts-details.component");
          return m.MaintenanceContractsDetailsComponent;
        }
      },
      {
        // Checklist condivise: la vista di dettaglio è una sidenav che si apre dalla riga, non
        // una rotta propria, quindi non esiste un path `/:id`.
        path: UrlCollection.sharedChecklists,
        canActivate: [PermissionGuardFn],
        data: { action: AppActions.VIEW_SHARED_CHECKLISTS },
        loadComponent: async () => {
          const m = await import("./pages/shared-checklists-page/shared-checklists-page.component");
          return m.SharedChecklistsPageComponent;
        }
      }
    ]
  },
  { path: "**", redirectTo: UrlCollection.notFound }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true, scrollPositionRestoration: "top" })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
